import type { EmailActivity } from '@common/types/activities';
import type { ProspectResponse } from '@common/types/prospects';
import type { TemplateFormField } from '@common/types/templateFormFields';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    intakeEmailReminderTemplateQueryOptions,
    quoteEmailReminderTemplateQueryOptions,
} from '@/queries/templates';
import {
    getAllEmailActivities,
    getIntakeFormResponse,
    getProspectById,
} from '@/services/ProspectService';
import {
    createVerificationDetails,
    getVerificationRequestResponse,
} from '@/services/TreefortService';

export function prospectDetailsQueryKey(prospectId?: string, versionId?: number | null) {
    return ['prospect', prospectId, versionId ?? null] as const;
}

export function useProspectDetails(prospectId?: string, versionId?: number | null) {
    return useQuery({
        queryKey: prospectDetailsQueryKey(prospectId, versionId),
        queryFn: async () => {
            const response = await getProspectById(prospectId as string, versionId);
            return response.data;
        },
        enabled: !!prospectId,
        placeholderData: keepPreviousData,
    });
}

export function useEmailTimeline(prospectId?: string) {
    return useQuery<EmailActivity[]>({
        queryKey: ['email-timeline', prospectId],
        queryFn: async () => {
            if (!prospectId) return [];
            const response = await getAllEmailActivities(prospectId);
            return response.data;
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });
}

export function useProspectIntakeForm(prospectId?: string) {
    return useQuery({
        queryKey: ['prospect-intake-form', prospectId],
        queryFn: async () => {
            const response = await getIntakeFormResponse(prospectId as string);
            return { status: response.status, data: response.data };
        },
        enabled: !!prospectId,
    });
}

export function useVerificationRequests(prospectId?: string, enabled = true) {
    return useQuery({
        queryKey: ['prospect-verification-requests', prospectId],
        queryFn: async () => {
            const response = await getVerificationRequestResponse(prospectId as string);
            return response.data;
        },
        enabled: !!prospectId && enabled,
    });
}

export interface VerificationStatusDetail {
    requestStatus: string;
    email?: string;
    requesterClientRequestId: string;
}

interface VerificationRequestItem {
    clientRequestId: string;
}

export function useVerificationStatuses(
    prospectId: string | undefined,
    verificationRequests: VerificationRequestItem[],
    enabled: boolean,
) {
    const requestIds = verificationRequests.map((request) => request.clientRequestId);

    return useQuery({
        queryKey: ['prospect-verification-statuses', prospectId, requestIds],
        queryFn: async () => {
            const responses = await Promise.all(
                verificationRequests.map(async (item) => {
                    try {
                        const response = await createVerificationDetails(item.clientRequestId);
                        const responseData = response?.data;

                        return {
                            requesterClientRequestId:
                                responseData?.requesterClientRequestId ?? item.clientRequestId,
                            requestStatus: responseData?.requestStatus || 'Unknown',
                            email: responseData?.profile?.email,
                        };
                    } catch (error) {
                        console.error(`Error fetching details for ${item.clientRequestId}:`, error);
                        return {
                            requesterClientRequestId: item.clientRequestId,
                            requestStatus: 'Error',
                        };
                    }
                }),
            );

            return Object.fromEntries(
                responses.map((response) => [response.requesterClientRequestId, response]),
            ) as Record<string, VerificationStatusDetail>;
        },
        enabled: !!prospectId && verificationRequests.length > 0 && enabled,
    });
}

export function useQuoteEmailReminder(quoteTemplateId?: number | null) {
    return useQuery({
        ...quoteEmailReminderTemplateQueryOptions(quoteTemplateId),
        select: (template) => template.isQuoteReminderOn,
    });
}

export function useIntakeEmailReminder(quoteTemplateId?: number | null) {
    return useQuery({
        ...intakeEmailReminderTemplateQueryOptions(quoteTemplateId),
        select: (template) => template.isQuoteReminderOn,
    });
}

export function buildSummaryDynamicFields(
    quoteFormFields: TemplateFormField[],
    clientQuoteResponse: ProspectResponse['clientQuoteResponse'],
): TemplateFormField[] {
    if (!clientQuoteResponse?.length) return [];

    const filteredFields = quoteFormFields
        .filter((field) =>
            clientQuoteResponse.some((resp) => (resp ? resp.name === field.name : false)),
        )
        .map((field) => {
            const quoteFormRespValue = clientQuoteResponse.find((resp) => resp.name === field.name);
            return {
                ...field,
                isVisibleToUser: field.isVisibleToUser,
                questionSchema: {
                    ...field.questionSchema,
                    value: quoteFormRespValue?.response,
                },
            };
        });

    const filteredFieldsWithVisibilityMod = filteredFields.map((field) => {
        const conditionField = filteredFields.find(
            (filteredField) => filteredField.name === field.questionSchema.conditionedBy,
        );
        const isVisibleToUser =
            conditionField &&
            conditionField.questionSchema.value === field.questionSchema.conditionValue;
        return {
            ...field,
            isVisibleToUser: isVisibleToUser || field.isVisibleToUser,
        };
    });

    const newConditionalFields: TemplateFormField[] = [];
    filteredFieldsWithVisibilityMod.forEach((field) => {
        if (field.questionSchema?.conditions?.length) {
            field.questionSchema.conditions.forEach(
                (condition: { nextQuestion?: { name?: string } }) => {
                    const isConditionalFieldAvailable = filteredFieldsWithVisibilityMod.some(
                        (f) => f.name === condition?.nextQuestion?.name,
                    );
                    if (!isConditionalFieldAvailable) {
                        const newField = quoteFormFields.find(
                            (f) => f.name === condition?.nextQuestion?.name,
                        );
                        if (newField) {
                            newConditionalFields.push(newField);
                        }
                    }
                },
            );
        }
    });

    return [...filteredFieldsWithVisibilityMod, ...newConditionalFields];
}
